import React, { useState, useEffect } from "react";
import { FormControl, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { toast } from "react-toastify";
import ReportTable from "./ReportTable";
import { axios_tenant } from "../../../../axios";
import { downloadReportXlsx, isReportExportError } from "./reportExportApi";
import { KOT_DISPLAY_DATE_FORMAT, formatKotDate, parseKotStoredDate, toKotStorageDate } from "../utils/dateHelpers";

const REPORT_ENDPOINT = "/kot/reports/licensing";

const LicensingSummaryReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [clientFilter, setClientFilter] = useState("");
  const [licensedToFilter, setLicensedToFilter] = useState("");
  const [lastUpdatedFilter, setLastUpdatedFilter] = useState("");

  // Shared by the list fetch and the export so the two can never drift apart - the download
  // has to reflect exactly the filters that produced what is on screen.
  const filters = {
    clientName: clientFilter,
    licensedTo: licensedToFilter,
    lastUpdated: lastUpdatedFilter,
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios_tenant.get(REPORT_ENDPOINT, { params: filters });
      setData((response?.data || []).map((row, index) => ({ id: index, ...row })));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientFilter, licensedToFilter, lastUpdatedFilter]);

  const handleDownload = async (exportFields) => {
    try {
      setLoading(true);
      await downloadReportXlsx(REPORT_ENDPOINT, filters, exportFields, "licensing_report.xlsx");
    } catch (error) {
      if (isReportExportError(error)) toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "clientName", headerName: "Client Name", width: 150 },
    { field: "clientNumber", headerName: "Client Number", width: 150 },
    { field: "usptoCustomerNumber", headerName: "USPTO Customer No.", width: 180 },
    { field: "familyNo", headerName: "Licensed Family No(s)", width: 180 },
    { field: "licensee", headerName: "Party Name", width: 150 },
    { field: "contactName", headerName: "Licensing Contact Name", width: 220 },
    { field: "email", headerName: "Licensing Email", width: 200 },
    {
      field: "lastUpdatedOn",
      headerName: "Last Updated On",
      width: 150,
      renderCell: (params) => <span>{formatKotDate(params.row.lastUpdatedOn)}</span>,
    },
  ];
  
  const filtersNode = (
    <>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <TextField
          size="small"
          label="Client Name"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
        />
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <TextField
          size="small"
          label="Party Name"
          value={licensedToFilter}
          onChange={(e) => setLicensedToFilter(e.target.value)}
        />
      </FormControl>
      <FormControl size="small">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Last Updated"
            value={parseKotStoredDate(lastUpdatedFilter)}
            onChange={(newValue) => setLastUpdatedFilter(newValue ? toKotStorageDate(newValue) : "")}
            inputFormat={KOT_DISPLAY_DATE_FORMAT}
            renderInput={(pickerParams) => <TextField {...pickerParams} size="small" onKeyDown={(e) => {
              if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                e.preventDefault();
              }
            }} />}
          />
        </LocalizationProvider>
      </FormControl>
    </>
  );

  return (
    <ReportTable
      title="Reports"
      columns={columns}
      data={data}
      loading={loading}
      filtersNode={filtersNode}
      onDownload={handleDownload}
    />
  );
};

export default LicensingSummaryReport;
